/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, CardContent, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Cancel, Edit } from "@mui/icons-material";
import { unassignAsset } from "./assetsFunctions";
import { conditionColor, conditionToName } from "../../utils/Constants";
import Card from "@mui/material/Card";
import { Asset, AssetAssignment, Effect, Setter, User } from "../../utils/Interfaces";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../app/hooks";

export interface AssetAssignmentCardProps {
    user: User;
    assignment: AssetAssignment;
    key: string | number;
    onAssetUnassigned: Effect;
    onOpenModifyAssetModal: Setter<Asset>;
}

const AssetAssignmentCard = (props: AssetAssignmentCardProps) => {
    const { user, assignment, key, onAssetUnassigned, onOpenModifyAssetModal } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);

    let asset: Asset = assignment.asset;

    return (
        <Card key={key} sx={{ mb: 3 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">{asset.name}</Typography>
                    {loading && <CircularProgress size={16} sx={{ mt: 1, mr: 1, mb: 2 }} />}
                    {!loading && (
                        <Stack direction="row" justifyContent="flex-end">
                            <Tooltip title={`Edit properties for asset ${asset.name}`}>
                                <IconButton onClick={() => onOpenModifyAssetModal(asset)}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={`Unassign ${asset.name} from ${user.first_name} ${user.last_name}`}>
                                <IconButton
                                    onClick={() => {
                                        unassignAsset(assignment.id, dispatch, onAssetUnassigned, setLoading);
                                    }}
                                >
                                    <Cancel />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Typography>{assignment.asset.manufacturer}</Typography>
                    <Typography color={conditionColor(assignment.asset.condition)}>
                        {conditionToName(assignment.asset.condition)}
                    </Typography>
                </Stack>
                <Box sx={{ mb: 2 }} />
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">{`Part #: ${assignment.asset.part_number}`}</Typography>
                    <Typography variant="subtitle2">Unit Price</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">{`Serial #: ${assignment.asset.serial_number ?? "--"}`}</Typography>
                    <Typography fontWeight="bold">${assignment.asset.price}</Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default AssetAssignmentCard;
